// Timezone utility functions
let storedUserTimezone = null;

export const setUserTimezone = (timezone) => {
  // Ensure we never store null/undefined/empty timezone
  if (timezone && timezone.trim() !== '') {
    storedUserTimezone = timezone.trim();
  } else {
    storedUserTimezone = null;
  }
};

export const getUserTimezone = () => {
  // First check if user has a stored preference
  if (storedUserTimezone && storedUserTimezone !== 'null') {
    return storedUserTimezone;
  }
  
  try {
    // Fallback to browser-detected timezone
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return browserTimezone || 'UTC';
  } catch (error) {
    console.warn('Could not detect user timezone, falling back to UTC');
    return 'UTC';
  }
};

export const formatDateInUserTimezone = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    const userTimezone = getUserTimezone();
    
    const defaultOptions = {
      year: 'numeric',
      month: 'short',  
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: userTimezone
    };

    const formatOptions = { ...defaultOptions, ...options };
    
    return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return new Date(dateString).toLocaleString();
  }
};

export const formatRelativeTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    // Ensure we're working with UTC timestamps consistently
    const date = new Date(dateString);
    const now = new Date();
    
    // Validate that we have valid dates
    if (isNaN(date.getTime()) || isNaN(now.getTime())) {
      return 'Invalid date';
    }
    
    // Calculate difference in milliseconds, then convert to seconds
    const diffInMs = now.getTime() - date.getTime();
    const diffInSeconds = Math.floor(diffInMs / 1000);
    
    // Handle future dates (shouldn't happen but good to be safe)
    if (diffInSeconds < 0) {
      return 'Just now';
    }
    
    if (diffInSeconds < 60) {
      return diffInSeconds <= 1 ? 'Just now' : `${diffInSeconds} seconds ago`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
      return formatDateInUserTimezone(dateString, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  } catch (error) {
    console.warn('Error formatting relative time:', error, 'Input:', dateString);
    return 'Unknown time';
  }
};

export const formatDateCompact = (dateString) => {
  return formatDateInUserTimezone(dateString, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatDateFull = (dateString) => {
  return formatDateInUserTimezone(dateString, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'long'
  });
};